import React, { useState, useMemo } from "react";
import { Box, Button, Card, CardActionArea, CardContent, Grid, InputAdornment, Paper, Stack, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { usePlatform } from "../platform/store";
import { PageRoot, StatusChip, SectionCard, SecondaryAction } from "./ui/PlatformPrimitives";

const PAGE_SIZE = 12;

// Translucent severity rail tints — Liquid Glass rule 7: status indicators
// must be rgba (not solid color blocks) so the rail composes with the glass
// surface below instead of stamping a hard colour bar.
const statusBarColor = (s) => {
  const v = String(s || "").toLowerCase();
  if (["ready", "clear", "completed", "compliant"].includes(v)) return "rgba(45,106,79,0.75)";
  if (["blocked", "failed"].includes(v)) return "rgba(153,27,27,0.85)";
  if (["review", "pending"].includes(v)) return "rgba(146,64,14,0.75)";
  return "rgba(0,0,0,0.10)";
};

const PlatformOverview = () => {
  const theme = useTheme();
  const { dashboard, products, issues } = usePlatform();
  const blockingIssues = issues.filter((i) => i.blocking);
  const blockerCount = blockingIssues.length;
  const firstBlockedProductId = blockingIssues[0]?.productId;
  const blockerLink = blockerCount === 1 && firstBlockedProductId
    ? `/perfume-dashboard?product=${firstBlockedProductId}`
    : "/compliance-tracker";
  const blockedProductCount = new Set(blockingIssues.map((i) => i.productId)).size;

  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    if (!search.trim()) return products;
    const q = search.toLowerCase();
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.brand?.toLowerCase().includes(q) ||
        p.markets?.some((m) => m.toLowerCase().includes(q))
    );
  }, [products, search]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  return (
    <PageRoot>
      {/* Hero — single line */}
      {blockerCount > 0 ? (
        <Paper
          component={RouterLink}
          to={blockerLink}
          sx={{
            display: "flex", alignItems: "center", gap: 2,
            px: 3, py: 2.5,
            // Hero/chrome tier radius (18) — full-width banner, no parent chrome.
            borderRadius: 2.25,
            // Translucent error tint layered over the glass — the surface
            // beneath still refracts the page gradient so the wet rim reads.
            background: "rgba(153,27,27,0.14)",
            textDecoration: "none", cursor: "pointer",
            animation: "fadeIn 300ms ease both",
            transition: theme.motion.morph,
            "&:hover": { background: "rgba(153,27,27,0.20)" },
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: 999,
              // Translucent error tint + matching ring so the dot reads as a
              // liquid-glass indicator rather than a solid red pixel block.
              bgcolor: "rgba(153,27,27,0.14)",
              boxShadow: "inset 0 0 0 1.5px rgba(153,27,27,0.40)",
            }}
          />
          <Typography variant="body1" sx={{ color: "#991B1B", fontWeight: 600, flex: 1 }}>
            {blockerCount} blocking issue{blockerCount !== 1 ? "s" : ""}
            <Typography component="span" variant="body1" sx={{ color: "text.secondary", fontWeight: 400, ml: 1 }}>
              · {blockedProductCount} product{blockedProductCount !== 1 ? "s" : ""}
            </Typography>
          </Typography>
        </Paper>
      ) : (
        <Paper
          sx={{
            display: "flex", alignItems: "center", gap: 2,
            px: 3, py: 2.5,
            // Hero/chrome tier radius (18) — matches the blocking hero variant.
            borderRadius: 2.25,
            animation: "fadeIn 300ms ease both",
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: 999,
              // Translucent success tint + ring (rule 7) so the all-clear dot
              // composes with the glass surface instead of solid green.
              bgcolor: "rgba(45,106,79,0.14)",
              boxShadow: "inset 0 0 0 1.5px rgba(45,106,79,0.40)",
            }}
          />
          <Typography variant="body1" sx={{ color: "text.primary", flex: 1 }}>
            All clear
            <Typography component="span" variant="body1" sx={{ color: "text.disabled", ml: 1 }}>
              · {dashboard.readyProducts} of {dashboard.products} release ready
            </Typography>
          </Typography>
          <Button variant="outlined" size="small" component={RouterLink} to="/compliance-checker" sx={{ borderRadius: 999 }}>
            New check
          </Button>
        </Paper>
      )}

      {/* Stats — generous landing-tier stat cards. Glass refracts through; the
          numeric value carries the only accent color. */}
      <Grid container spacing={2}>
        {[
          {
            label: "Products",
            value: dashboard.readyProducts,
            helper: `of ${dashboard.products}`,
            to: "/perfume-dashboard",
            // Success/error are read off theme.palette so stat values stay on
            // the semantic palette instead of hard-coded hex shades.
            color:
              dashboard.readyProducts === dashboard.products
                ? theme.palette.success.main
                : "text.primary",
          },
          {
            label: "Blocking",
            value: dashboard.blockingIssues,
            to: "/compliance-tracker",
            color:
              dashboard.blockingIssues > 0
                ? theme.palette.error.main
                : "text.primary",
          },
          {
            label: "Documents",
            value: dashboard.documents,
            to: "/document-repository",
            color: "text.primary",
          },
          {
            label: "Vendors",
            value: dashboard.vendors,
            to: "/vendor-management",
            color: "text.primary",
          },
        ].map((stat) => (
          <Grid key={stat.label} size={{ xs: 6, sm: 3 }}>
            <Paper
              component={RouterLink}
              to={stat.to}
              sx={{
                // p: 3 keeps stat values airy (rule 4: stat values need air).
                p: 3,
                display: "block",
                textDecoration: "none",
                // Surface tier radius (16) — concentric default for cards.
                borderRadius: 2,
                transition: theme.motion.morph,
                "&:hover": { transform: "translateY(-1px)" },
              }}
            >
              <Typography variant="overline" sx={{ display: "block", color: "text.secondary" }}>
                {stat.label}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mt: 1 }}>
                <Typography variant="h3" sx={{ fontVariantNumeric: "tabular-nums", color: stat.color, lineHeight: 1 }}>
                  {stat.value}
                </Typography>
                {stat.helper && (
                  <Typography variant="caption" sx={{ color: "text.disabled" }}>{stat.helper}</Typography>
                )}
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Products */}
      <SectionCard
        title="Products"
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              placeholder="Search…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setVisibleCount(PAGE_SIZE); }}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 16, color: "rgba(0,0,0,0.45)" }} /></InputAdornment>,
                },
              }}
              sx={{ width: 200 }}
            />
            <Button variant="outlined" size="small" component={RouterLink} to="/compliance-checker" sx={{ borderRadius: 999 }}>
              New formula
            </Button>
          </Stack>
        }
      >
        {visible.length === 0 ? (
          <Box sx={{ py: 5, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {search ? "No products match your search" : "No products yet"}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {visible.map((p) => {
              const isBlocked = p.compliance.blockingIssueCount > 0;
              return (
                <Grid key={p.id} size={{ xs: 12, sm: 6, xl: 4 }}>
                  <Card
                    sx={{
                      height: "100%",
                      position: "relative",
                      overflow: "hidden",
                      // Surface tier (16) — concentric inside the SectionCard chrome.
                      borderRadius: 2,
                    }}
                  >
                    <Box sx={{ position: "absolute", left: 0, top: 0, right: 0, height: 3, bgcolor: statusBarColor(p.compliance.overallStatus), zIndex: 1 }} />
                    <CardActionArea component={RouterLink} to={`/perfume-dashboard?product=${p.id}`} sx={{ height: "100%", borderRadius: "inherit" }}>
                      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                        <Stack spacing={2} sx={{ height: "100%" }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                            <Box sx={{ minWidth: 0 }}>
                              {p.brand && (
                                <Typography
                                  variant="overline"
                                  sx={{
                                    display: "block",
                                    // Navy-derived vibrancy tier for the brand eyebrow.
                                    color: "rgba(27,42,74,0.60)",
                                    mb: 0.5,
                                  }}
                                >
                                  {p.brand}
                                </Typography>
                              )}
                              <Typography
                                variant="h5"
                                sx={{
                                  color: "text.primary",
                                  lineHeight: 1.25,
                                }}
                              >
                                {p.name}
                              </Typography>
                            </Box>
                            <StatusChip status={p.compliance.overallStatus} />
                          </Stack>

                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {p.markets.map((market) => (
                              <Typography
                                key={market}
                                variant="caption"
                                sx={{
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 999,
                                  background: theme.glass.inset.background,
                                  backdropFilter: theme.glass.inset.backdropFilter,
                                  WebkitBackdropFilter: theme.glass.inset.WebkitBackdropFilter,
                                  border: theme.glass.inset.border,
                                  fontWeight: 600,
                                  letterSpacing: "0.04em",
                                  // Navy-derived rgba — pill text on inset glass.
                                  color: "rgba(27,42,74,0.78)",
                                }}
                              >
                                {market}
                              </Typography>
                            ))}
                          </Stack>

                          <Box sx={{ mt: "auto" }}>
                            <Typography
                              variant="caption"
                              sx={{
                                // Allowlisted severity contrast colour; otherwise body-secondary.
                                color: isBlocked ? "#991B1B" : "text.secondary",
                                fontWeight: isBlocked ? 600 : 500,
                              }}
                            >
                              {isBlocked
                                ? `${p.compliance.blockingIssueCount} blocking issue${p.compliance.blockingIssueCount === 1 ? "" : "s"}`
                                : "No blocking issues"}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
        {hasMore && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <SecondaryAction size="small" onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}>
              Show more ({filtered.length - visibleCount})
            </SecondaryAction>
          </Box>
        )}
      </SectionCard>
    </PageRoot>
  );
};

export default PlatformOverview;
